import {defineMessages, type IntlShape} from 'react-intl'

import {
  isRoomlessRoomName,
  isUnnamedRoomName
} from '@vio/core/src/room/business/utils/roomless'

const messages = defineMessages({
  roomForGuests: {
    id: 'room.roomForGuests',
    defaultMessage: 'Room for {guests, plural, one {# guest} other {# guests}}'
  },
  roomsForGuests: {
    id: 'room.roomsForGuests',
    defaultMessage: 'Rooms for {guests, plural, one {# guest} other {# guests}}'
  },
  yourRoomForTheStay: {
    id: 'room.yourRoomForTheStay',
    defaultMessage: 'Your room for the stay'
  }
})

type FormatMessage = IntlShape['formatMessage']

export interface SearchOccupancy {
  /** Adults and children across the whole search. */
  numberOfGuests: number
  numberOfRooms: number
}

/** "Room for 2 guests", the name shown in place of a hidden room. */
export const getGeneratedRoomName = (
  formatMessage: FormatMessage,
  {numberOfGuests, numberOfRooms}: SearchOccupancy
): string =>
  formatMessage(
    numberOfRooms > 1 ? messages.roomsForGuests : messages.roomForGuests,
    {guests: numberOfGuests}
  )

// TODO [sc-198186]: remove once RAA returns the translated room name.
/**
 * Roomless rooms arrive without a name, so the guest sees one built from the
 * search instead. The legacy placeholder comes untranslated from RAA, so it is
 * swapped for our own copy.
 * @param formatMessage - From the active intl instance.
 * @param roomName - Name as it arrives from RAA.
 * @param occupancy - Guests and rooms of the search.
 */
export const getRoomDisplayName = (
  formatMessage: FormatMessage,
  roomName: string | null | undefined,
  occupancy: SearchOccupancy
): string => {
  if (!isRoomlessRoomName(roomName)) return roomName ?? ''

  if (isUnnamedRoomName(roomName))
    return getGeneratedRoomName(formatMessage, occupancy)

  return formatMessage(messages.yourRoomForTheStay)
}
